import React from "react";
import { useLocation } from "react-router-dom";
import {
  COACH_EXERCISE_LIBRARY_PUBLISHED_ROUTE,
  COACH_EXERCISE_LIBRARY_TAGS_ROUTE,
  COACH_PROGRAMS_FOLDERS_ROUTE,
} from "../../../routes/routes";
import {
  BodypartType,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import { CoachExerciseLibraryHeaderFilters } from "../../../components/exercise-library/CoachExerciseLibraryHeaderFilters";
import {
  ALL_OPTIONS,
  ALL_OPTIONS_TYPE,
  ExerciseListFilterContext,
} from "../../../contexts/ExerciseLibraryFilterContext";
import {
  allBodyparts,
  allBodypartsSelected,
  allMovementPatterns,
  allMovementPatternsSelected,
} from "../../../components/exercise-library/ExercisesTableScreen";
import { useQuery } from "@tanstack/react-query";
import ExerciseAssetsService from "../../../services/ExerciseAssetsService";
import { useCoachTabsContext } from "../../../contexts/CoachTabsContext";
import { CoachLibraryLayout } from "./CoachLibraryLayout";
import {
  useLibraryFiltersState,
  BaseLibraryFilters,
} from "../../../hooks/useLibraryFilterState";

export interface ExerciseLibraryFilters extends BaseLibraryFilters {
  bodyparts: BodypartType[] | ALL_OPTIONS_TYPE;
  movementPatterns: MovementPatternType[] | ALL_OPTIONS_TYPE;
  tagIds: number[] | null | ALL_OPTIONS_TYPE;
}

export const EXERCISE_LIBRARY_FILTERS_KEY = "exerciseLibraryFilters";

export const COACH_EXERCISE_LIBRARY_ASSETS_COUNT_QUERY_KEY =
  "coach-exercise-library-assets-count";

const CoachExerciseLibraryWrapper = () => {
  const location = useLocation();

  const { setExerciseAssetsCount } = useCoachTabsContext();

  const { data: exerciseAssetsCount } = useQuery({
    queryKey: [COACH_EXERCISE_LIBRARY_ASSETS_COUNT_QUERY_KEY],
    queryFn: () => ExerciseAssetsService.getExerciseAssetsCount(),
  });

  React.useEffect(() => {
    exerciseAssetsCount && setExerciseAssetsCount(exerciseAssetsCount);
  }, [exerciseAssetsCount]);

  const {
    query,
    sortKey,
    filters,
    page,
    rowsPerPage,
    privacy,
    handleQueryChange,
    handleSortKeyChange,
    updateFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleChangePrivacy,
    handleResetFilters,
    filtersToReset,
  } = useLibraryFiltersState<ExerciseLibraryFilters>({
    defaultFilters: {
      bodyparts: ALL_OPTIONS,
      movementPatterns: ALL_OPTIONS,
      tagIds: ALL_OPTIONS,
    },
    resetOn: location.pathname,
  });

  const { bodyparts, movementPatterns, tagIds } = filters;

  const handleBodypartChange = React.useCallback(
    (bodyparts: BodypartType[]) => {
      updateFilters({
        bodyparts: allBodypartsSelected(bodyparts) ? ALL_OPTIONS : bodyparts,
      });
    },
    [updateFilters],
  );

  const handleMovementPatternChange = React.useCallback(
    (movementPatterns: MovementPatternType[]) => {
      updateFilters({
        movementPatterns: allMovementPatternsSelected(movementPatterns)
          ? ALL_OPTIONS
          : movementPatterns,
      });
    },
    [updateFilters],
  );

  const handleTagIdsChange = React.useCallback(
    (tagIds?: number[], allOptionsSelected?: boolean) => {
      updateFilters({ tagIds: allOptionsSelected ? ALL_OPTIONS : tagIds });
    },
    [updateFilters],
  );

  const isTagsListRoute = location.pathname.includes(
    COACH_EXERCISE_LIBRARY_TAGS_ROUTE,
  );

  const filterContextValue = {
    query,
    sortKey,
    bodyparts: bodyparts == ALL_OPTIONS ? allBodyparts : bodyparts,
    movementPatterns:
      movementPatterns == ALL_OPTIONS ? allMovementPatterns : movementPatterns,
    privacy,
    tagIds,
    page,
    rowsPerPage,
    handleQueryChange,
    handleSortKeyChange,
    handleBodypartChange,
    handleMovementPatternChange,
    handleTagIdsChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleChangePrivacy,
  };

  const showHeader = location.pathname !== COACH_PROGRAMS_FOLDERS_ROUTE;

  return (
    <CoachLibraryLayout
      Provider={ExerciseListFilterContext.Provider}
      providerValue={filterContextValue}
      hideHeader={!showHeader}
      maxWidth="xl"
      header={
        showHeader ? (
          <CoachExerciseLibraryHeaderFilters
            query={query}
            isTagsRoute={isTagsListRoute}
            onChangeQuery={handleQueryChange}
            privacy={privacy}
            onChangePrivacy={handleChangePrivacy}
            disabledResetButton={!filtersToReset}
            showResetButton={true}
            onResetFilters={handleResetFilters}
            showFilterByType={
              location.pathname == COACH_EXERCISE_LIBRARY_PUBLISHED_ROUTE
            }
          />
        ) : undefined
      }
    />
  );
};

export default CoachExerciseLibraryWrapper;
